import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Shell from "@/components/Shell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { History, Plus, Sparkles, ImageOff, Heart } from "lucide-react";
import { BRAND } from "@/lib/brand";

export default function MRDashboard() {
  const { user } = useAuth();
  const [posters, setPosters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/posters").then(({ data }) => setPosters(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const recent = posters.filter((p) => !p.deleted).slice(0, 20);

  return (
    <Shell role="mr">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Dashboard</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-brand-ink">
            Hello, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-slate-500 mt-1">{`Generate a personalized ${BRAND.eventName} poster in under a minute.`}</p>
        </div>
        <Link to="/mr/generate" data-testid="cta-generate-poster">
          <Button className="h-12 px-6 bg-brand-600 hover:bg-brand-700 gap-2 shadow-lg shadow-brand-600/20">
            <Plus className="w-4 h-4" /> Generate New Poster
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
        <Card className="p-6">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Posters Created</div>
          <div className="font-display text-4xl font-extrabold mt-2" data-testid="stat-poster-count">
            {posters.filter((p) => !p.deleted).length}
          </div>
        </Card>
        <Card className="p-6">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Last 7 Days</div>
          <div className="font-display text-4xl font-extrabold mt-2">
            {posters.filter((p) => !p.deleted && new Date(p.created_at) > new Date(Date.now() - 7 * 86400000)).length}
          </div>
        </Card>
        <Card className="p-6 bg-gradient-to-br from-brand-600 to-brand-800 text-white border-brand-700 relative overflow-hidden">
          <Heart className="absolute -right-3 -bottom-3 w-24 h-24 opacity-15 fill-current" aria-hidden="true" />
          <div className="relative text-xs font-bold uppercase tracking-[0.2em] text-brand-100">Campaign</div>
          <div className="relative font-display text-xl font-extrabold mt-2 leading-tight">{BRAND.eventDate}<br/>{BRAND.eventName}</div>
        </Card>
      </div>

      {/* Recent posters table */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl font-bold text-brand-ink">Recent Posters</h2>
        <Link to="/mr/history" className="text-sm font-semibold text-slate-600 hover:text-brand-700 inline-flex items-center gap-1">
          View all <History className="w-4 h-4" />
        </Link>
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm">Loading…</div>
      ) : recent.length === 0 ? (
        <Card className="p-10 text-center border-dashed">
          <div className="mx-auto w-12 h-12 rounded-full bg-slate-100 grid place-items-center mb-3">
            <Sparkles className="w-6 h-6 text-slate-500" />
          </div>
          <div className="font-display text-lg font-bold text-slate-900 mb-1">No posters yet</div>
          <div className="text-sm text-slate-500 mb-4">Create your first personalized poster now.</div>
          <Link to="/mr/generate"><Button data-testid="empty-state-generate">Generate Poster</Button></Link>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Doctor Name</th>
                  <th className="px-4 py-3">Qualification</th>
                  <th className="px-4 py-3">Hospital / Clinic</th>
                  <th className="px-4 py-3">Mobile</th>
                  <th className="px-4 py-3">Language</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">File</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`poster-row-${p.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900 whitespace-nowrap">{p.doctor_name}</td>
                    <td className="px-4 py-3 text-slate-600">{p.qualification || "—"}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-[200px] truncate">{p.hospital || "—"}</td>
                    <td className="px-4 py-3 text-slate-500">{p.mobile || "—"}</td>
                    <td className="px-4 py-3">
                      <span className="inline-block px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs uppercase">
                        {p.language_code}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                      {new Date(p.created_at).toLocaleDateString()}{" "}
                      <span className="text-xs text-slate-400">
                        {new Date(p.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {p.file_deleted ? (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                          <ImageOff className="w-3 h-3" /> Removed
                        </span>
                      ) : (
                        <span className="inline-block w-2 h-2 rounded-full bg-emerald-400" title="File available" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </Shell>
  );
}
